// The stock ledger (plan §5.3): every movement, newest first, with when it happened and
// when it was recorded, its trading day, the reason in plain words, the signed quantity,
// the source document and who recorded it (by name, on which station). Filters: product,
// why, where and trading days (review UI2A-11). Ids and keys sit under "Technical details"
// in the side panel, for support (UI2A-13). Replay movements are never here (D-036).

import type { StockMovement } from '@enp/domain/api';
import { isLateReceipt } from '@enp/domain/connections';
import type { ReasonCode } from '@enp/domain/ledger';
import {
  Badge,
  Button,
  createColumnHelper,
  DataTable,
  EmptyState,
  Input,
  PageHeader,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  type ColumnDef,
} from '@enp/ui';
import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { Clock, History, PackageOpen, Search, SearchX, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { meQueryOptions } from '../../../auth/session.js';
import { AccountDate } from '../../../shell/account-date.js';
import { useDocumentTitle } from '../../../shell/page.js';
import { ErrorPanel } from '../../../shell/route-states.js';
import { usePageSearch } from '../../../shell/signals.js';
import {
  DayRange,
  FilterSelect,
  LoadMoreFooter,
  SignedQty,
  StockStateBadge,
  StockViews,
} from '../../../stock/components.js';
import {
  ACTOR_LABEL,
  actorText,
  LOCATION_TYPE_LABEL,
  plainDateLabel,
  PROVENANCE_LABEL,
  REASON_LABEL,
  sourceDocLabel,
  sourceDocRef,
  accountToday,
} from '../../../stock/labels.js';
import {
  clean,
  locationsQuery,
  lotsQuery,
  type MovementsSearch,
  movementsQuery,
  movementsSearchSchema,
} from '../../../stock/queries.js';

/**
 * A movement's source document in words, "Receipt 1-2001", linked to the receipt when it
 * is one (review UI2A-215).
 */
function SourceDoc({ doc, today }: { doc: StockMovement['source_doc']; today: string }) {
  const text = (
    <>
      {sourceDocLabel(doc.type)} <span className="text-muted">{sourceDocRef(doc, today)}</span>
    </>
  );
  if (doc.type !== 'sale_ticket') return <span className="text-fg">{text}</span>;
  return (
    <Link
      to="/sales/$ticketId"
      params={{ ticketId: doc.id }}
      // The row opens the movement; the link opens the receipt.
      onClick={(e) => e.stopPropagation()}
      className="rounded-sm text-primary-text underline-offset-2 hover:underline [&_span]:text-primary-text"
    >
      {text}
    </Link>
  );
}

export const Route = createFileRoute('/_app/stock/movements')({
  validateSearch: movementsSearchSchema,
  component: MovementsPage,
});

const TABLE_LABEL = 'Stock movements';

/** Reasons offered in the filter: real stock only (replay reasons never appear here). */
const REASON_FILTER: ReasonCode[] = [
  'sale',
  'sale_reversal',
  'refund_return',
  'fulfilment',
  'receipt',
  'production_output',
  'production_consumption',
  'state_change',
  'count_adjustment',
  'write_off',
  'transfer_out',
  'transfer_in',
  'correction',
  'opening',
];

/** Recorded late (an offline station catching up): the one domain rule (D-038, CR2-26). */
const recordedLate = (m: StockMovement) => isLateReceipt(m.occurred_at, m.recorded_at);

function movementColumns(today: string): ColumnDef<StockMovement, unknown>[] {
  const col = createColumnHelper<StockMovement>();
  return [
    col.display({
      id: 'when',
      header: 'When',
      cell: ({ row }) => {
        const m = row.original;
        return (
          <span className="flex flex-col whitespace-nowrap leading-tight">
            <AccountDate value={m.occurred_at} withTime className="text-fg" />
            {recordedLate(m) ? (
              <span className="flex items-center gap-1 text-xs text-muted">
                <Clock aria-hidden className="size-3" />
                Recorded <AccountDate value={m.recorded_at} withTime />
              </span>
            ) : (
              <span className="text-xs text-muted">
                Trading day {plainDateLabel(m.trading_day, today)}
              </span>
            )}
          </span>
        );
      },
    }),
    col.display({
      id: 'product',
      header: 'Product',
      meta: { width: 'min-w-44' },
      cell: ({ row }) => (
        <span className="flex flex-col leading-tight">
          <span className="font-medium text-fg">{row.original.product.name}</span>
          {row.original.lot && (
            <span className="font-mono text-xs text-muted">{row.original.lot.lot_code}</span>
          )}
        </span>
      ),
    }),
    col.display({
      id: 'qty',
      header: 'Change',
      meta: { align: 'right' },
      cell: ({ row }) => (
        <SignedQty value={row.original.qty_delta} unit={row.original.product.base_uom} />
      ),
    }),
    col.display({
      id: 'reason',
      header: 'Why',
      // Below 1400 px the "how we know" line goes to a hover title and the details panel,
      // so a row stays two lines tall at 1280 (UI8B-4).
      cell: ({ row }) => (
        <span
          className="flex flex-col leading-tight"
          title={PROVENANCE_LABEL[row.original.provenance]}
        >
          <span className="text-fg">{REASON_LABEL[row.original.reason]}</span>
          <span className="hidden text-xs text-muted min-[1400px]:inline">
            {PROVENANCE_LABEL[row.original.provenance]}
          </span>
        </span>
      ),
    }),
    col.display({
      id: 'where',
      header: 'Where and state',
      meta: { className: 'hidden lg:table-cell' },
      cell: ({ row }) => (
        <span className="flex flex-col items-start gap-1 leading-tight">
          <span className="whitespace-nowrap text-sm text-fg">{row.original.location.name}</span>
          <StockStateBadge state={row.original.state} />
        </span>
      ),
    }),
    col.display({
      id: 'source',
      header: 'Source and by',
      meta: { className: 'hidden xl:table-cell' },
      cell: ({ row }) => (
        <span className="flex flex-col leading-tight">
          <span className="whitespace-nowrap text-sm">
            <SourceDoc doc={row.original.source_doc} today={today} />
          </span>
          <span className="text-xs text-muted">{actorText(row.original.actor)}</span>
        </span>
      ),
    }),
    col.display({
      id: 'details',
      header: () => <span className="sr-only">Details</span>,
      cell: () => (
        <span className="whitespace-nowrap text-sm font-medium text-primary-text">Details</span>
      ),
    }),
  ];
}

function Fact({ term, children }: { term: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5 border-b border-border py-2.5 last:border-0">
      <dt className="text-xs font-semibold uppercase tracking-wide text-muted">{term}</dt>
      <dd className="text-body text-fg">{children}</dd>
    </div>
  );
}

function MovementDetails({
  movement,
  today,
  onClose,
}: {
  movement: StockMovement | null;
  today: string;
  onClose: () => void;
}) {
  const m = movement;
  return (
    <Sheet open={m !== null} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="overflow-y-auto">
        {m && (
          <>
            <SheetHeader>
              <SheetTitle>{REASON_LABEL[m.reason]}</SheetTitle>
              <SheetDescription>
                {m.product.name} · <SignedQty value={m.qty_delta} unit={m.product.base_uom} />
              </SheetDescription>
            </SheetHeader>
            <dl className="flex flex-col px-6">
              <Fact term="Happened">
                <AccountDate value={m.occurred_at} withTime />
              </Fact>
              <Fact term="Recorded">
                <AccountDate value={m.recorded_at} withTime />
                {recordedLate(m) && (
                  <span className="block text-sm text-muted">
                    Later than it happened: the station was probably offline.
                  </span>
                )}
              </Fact>
              <Fact term="Trading day">{plainDateLabel(m.trading_day, today)}</Fact>
              <Fact term="Lot">
                {m.lot ? <span className="font-mono">{m.lot.lot_code}</span> : 'Not by lot'}
              </Fact>
              <Fact term="Where">
                {m.location.name} ({LOCATION_TYPE_LABEL[m.location.type]})
              </Fact>
              <Fact term="Expiry state">
                <StockStateBadge state={m.state} />
              </Fact>
              <Fact term="How we know">{PROVENANCE_LABEL[m.provenance]}</Fact>
              <Fact term="Source document">
                <SourceDoc doc={m.source_doc} today={today} />
              </Fact>
              <Fact term="Recorded by">
                {m.actor.name ?? ACTOR_LABEL[m.actor.type]}
                {m.actor.station && (
                  <span className="block text-sm text-muted">On {m.actor.station}</span>
                )}
              </Fact>
              {m.compensates_key && (
                // What it undoes, in words, with its receipt (UI2A-215).
                <Fact term="Reverses">
                  {m.compensates ? (
                    <>
                      {REASON_LABEL[m.compensates.reason]},{' '}
                      <SignedQty value={m.compensates.qty_delta} unit={m.product.base_uom} /> on{' '}
                      <AccountDate value={m.compensates.occurred_at} withTime />
                      {m.source_doc.type === 'sale_ticket' && (
                        <>
                          {' '}
                          (<SourceDoc doc={m.source_doc} today={today} />)
                        </>
                      )}
                    </>
                  ) : (
                    'An earlier movement that isn’t in the ledger any more (the key is under Technical details)'
                  )}
                </Fact>
              )}
            </dl>
            <details className="group mx-6 mb-6 mt-2 rounded-md border border-border">
              <summary className="cursor-pointer rounded-md px-4 py-2.5 text-sm font-medium text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring">
                Technical details
                <span className="font-normal text-muted"> (for support)</span>
              </summary>
              <dl className="flex flex-col border-t border-border px-4">
                <Fact term="Idempotency key">
                  <span className="break-all font-mono text-sm">{m.idempotency_key}</span>
                  <span className="block text-sm text-muted">
                    The same key is never recorded twice, so a retried entry can’t double count.
                  </span>
                </Fact>
                {m.compensates_key && (
                  <Fact term="Reverses key">
                    <span className="break-all font-mono text-sm">{m.compensates_key}</span>
                  </Fact>
                )}
                <Fact term="Source document id">
                  <span className="break-all font-mono text-sm">{m.source_doc.id}</span>
                </Fact>
                <Fact term="Recorded by (id)">
                  <span className="text-sm">{ACTOR_LABEL[m.actor.type]}</span>{' '}
                  <span className="break-all font-mono text-sm text-muted">{m.actor.id}</span>
                  {m.actor.device_id && (
                    <span className="block text-sm text-muted">
                      Device <span className="font-mono">{m.actor.device_id}</span>
                    </span>
                  )}
                </Fact>
                <Fact term="Movement id">
                  <span className="break-all font-mono text-sm">{m.id}</span>
                </Fact>
              </dl>
            </details>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

function MovementsPage() {
  useDocumentTitle('Stock movements');
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { data: me } = useSuspenseQuery(meQueryOptions());
  const today = accountToday(me.account);
  const [open, setOpen] = useState<StockMovement | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  usePageSearch(searchRef);

  const setSearch = (patch: Partial<MovementsSearch>) =>
    void navigate({ search: (prev) => clean({ ...prev, ...patch }), replace: true });

  // The product search types freely; the URL (and the server) follow after a pause.
  const [text, setText] = useState(search.q ?? '');
  const pushed = useRef(search.q ?? '');
  useEffect(() => {
    if ((search.q ?? '') !== pushed.current) {
      pushed.current = search.q ?? '';
      setText(search.q ?? '');
    }
  }, [search.q]);
  useEffect(() => {
    const q = text.trim();
    if (q === pushed.current) return;
    const timer = setTimeout(() => {
      pushed.current = q;
      void navigate({ search: (prev) => clean({ ...prev, q: q || undefined }), replace: true });
    }, 300);
    return () => clearTimeout(timer);
  }, [text, navigate]);

  const list = useInfiniteQuery({ ...movementsQuery(search), placeholderData: keepPreviousData });
  const items = useMemo(() => list.data?.pages.flatMap((p) => p.items) ?? [], [list.data]);
  const locations = useQuery(locationsQuery());
  // The filtered product's name, even when it has no movements in the range.
  const product = useQuery({
    ...lotsQuery(search.product ?? ''),
    enabled: Boolean(search.product),
  });
  const productName = product.data?.product.name ?? items[0]?.product.name;
  const lotCode = search.lot
    ? items.find((m) => m.lot?.id === search.lot)?.lot?.lot_code
    : undefined;
  const filtered = Boolean(
    search.product ||
    search.lot ||
    search.q ||
    search.reason ||
    search.location ||
    search.from ||
    search.to,
  );
  const columns = useMemo(() => movementColumns(today), [today]);
  const clearAll = () => {
    pushed.current = '';
    setText('');
    void navigate({ search: {} });
  };

  const firstLoad = list.isPending;
  const failed = list.isError && !list.data;

  const chip = (label: ReactNode, clear: () => void, name: string) => (
    <Badge tone="primary" className="gap-1.5 py-1 pl-3 pr-1">
      {label}
      <button
        type="button"
        aria-label={`Remove the ${name} filter`}
        onClick={clear}
        className="inline-flex size-5 items-center justify-center rounded-full hover:bg-primary/15"
      >
        <X aria-hidden className="size-3.5" />
      </button>
    </Badge>
  );

  return (
    <>
      <PageHeader
        title="Stock"
        description="Every change to stock, newest first. Nothing is ever edited: a correction is a new movement."
      />
      <StockViews current="movements" />
      <div
        className="mb-3 flex flex-wrap items-center gap-2"
        role="search"
        aria-label="Movement filters"
      >
        {search.product ? (
          chip(
            <>
              Product: <span className="font-semibold">{productName ?? '…'}</span>
            </>,
            () => setSearch({ product: undefined, lot: undefined }),
            'product',
          )
        ) : (
          <div className="relative w-full max-w-56">
            <Search
              aria-hidden
              className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted"
            />
            <Input
              ref={searchRef}
              type="search"
              aria-label="Search products"
              aria-keyshortcuts="/"
              placeholder="Product name"
              value={text}
              onChange={(e) => setText(e.target.value)}
              className="pl-8"
            />
          </div>
        )}
        {search.lot &&
          chip(
            <>
              Lot: <span className="font-mono font-semibold">{lotCode ?? '…'}</span>
            </>,
            () => setSearch({ lot: undefined }),
            'lot',
          )}
        <FilterSelect
          label="Why"
          value={search.reason}
          allLabel="Any reason"
          options={REASON_FILTER.map((r) => ({ value: r, label: REASON_LABEL[r] }))}
          onChange={(v) => setSearch({ reason: v as ReasonCode | undefined })}
        />
        <FilterSelect
          label="Location"
          value={search.location}
          allLabel="All locations"
          options={(locations.data?.items ?? []).map((l) => ({
            value: l.id,
            label: l.name,
            group: l.branch.name,
          }))}
          onChange={(v) => setSearch({ location: v })}
        />
        <DayRange
          idPrefix="movements"
          from={search.from}
          to={search.to}
          today={today}
          onChange={(r) => setSearch({ from: r.from, to: r.to })}
        />
        {filtered && (
          <Button variant="ghost" icon={<X aria-hidden />} onClick={clearAll}>
            Clear
          </Button>
        )}
        {search.product && (
          <Button variant="link" asChild className="ml-auto">
            <Link to="/stock/$productId" params={{ productId: search.product }}>
              Lots on hand now
            </Link>
          </Button>
        )}
      </div>

      {failed ? (
        <ErrorPanel
          error={list.error}
          titleAs="h2"
          what="The movements"
          onRetry={() => void list.refetch()}
        />
      ) : (
        <DataTable
          label={TABLE_LABEL}
          columns={columns}
          data={items}
          loading={firstLoad}
          filterable={false}
          getRowId={(m) => m.id}
          onRowActivate={setOpen}
          empty={
            filtered ? (
              <EmptyState
                compact
                icon={SearchX}
                title="No movements match"
                description="Nothing moved for these filters. Widen the dates, or clear the filters."
                action={
                  <Button variant="secondary" icon={<X aria-hidden />} onClick={clearAll}>
                    Clear filters
                  </Button>
                }
              />
            ) : (
              <EmptyState
                icon={History}
                title="No stock movements yet"
                description="Deliveries, production, sales, counts and write-offs each add a movement here. What’s on hand now is under On hand."
                action={
                  <Button variant="secondary" asChild>
                    <Link to="/stock">
                      <PackageOpen aria-hidden />
                      See stock on hand
                    </Link>
                  </Button>
                }
              />
            )
          }
          footer={
            !firstLoad && items.length > 0 ? (
              <LoadMoreFooter
                loaded={items.length}
                noun={['movement', 'movements']}
                hasMore={list.hasNextPage}
                loading={list.isFetchingNextPage}
                updating={list.isFetching && !list.isFetchingNextPage}
                failed={list.isError && Boolean(list.data)}
                onMore={() => void list.fetchNextPage()}
              />
            ) : undefined
          }
          className="xl:max-h-[calc(100dvh-20rem)]"
        />
      )}
      <MovementDetails movement={open} today={today} onClose={() => setOpen(null)} />
    </>
  );
}
